import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import ClienteLayout from '../../components/ClienteLayout'
import TrustBadge from '../../components/TrustBadge'
import { useAuth } from '../../lib/AuthContext'
import { supabase } from '../../lib/supabase'
import {
  getMisSuscripciones, getDashboardResumen, getAhorroAcumulado,
} from '../../lib/portalClienteApi'

// Fallback estático si falla la generación con IA
const TIPS_FALLBACK = [
  { icono: '🌿', titulo: 'El bicarbonato es tu aliado', texto: 'Una cucharada en el lavarropas refuerza el detergente y neutraliza olores.' },
  { icono: '🍋', titulo: 'Limón para grasa', texto: 'Mezclá jugo de limón con bicarbonato para quitar grasa de la cocina sin químicos fuertes.' },
  { icono: '🧴', titulo: 'Vinagre + agua', texto: 'Mitad vinagre blanco, mitad agua. El multiuso natural más barato.' },
  { icono: '📦', titulo: 'Guardá los envases', texto: 'Cuando termina un producto, lavalo y dejalo airear. Reutilizable para mil cosas.' },
]

const SALUDOS = ['Buenos días', 'Buenas tardes', 'Buenas noches']

const TIER_LINEA = {
  essential: 'Línea Essential',
  premium:   'Línea Premium',
  comercial: 'Línea Comercial',
}

function getSaludo() {
  const h = new Date().getHours()
  if (h < 13) return SALUDOS[0]
  if (h < 20) return SALUDOS[1]
  return SALUDOS[2]
}

function fmtFecha(iso) {
  if (!iso) return null
  const d = new Date(iso + (iso.length === 10 ? 'T12:00:00' : ''))
  const dias = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb']
  const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
  return `${dias[d.getDay()]} ${d.getDate()} ${meses[d.getMonth()]}`
}

function fmtMonto(n) {
  return '$' + Number(n || 0).toLocaleString('es-UY', { maximumFractionDigits: 0 })
}

export default function Inicio() {
  const { user, profile } = useAuth()
  const [suscripciones, setSuscripciones] = useState([])
  const [resumen, setResumen] = useState(null)
  const [ahorro, setAhorro] = useState(null)
  const [tipDia, setTipDia] = useState(null)  // tip diario IA + producto
  const [cargando, setCargando] = useState(true)

  useEffect(() => {
    if (!user?.id) return
    cargar()
    cargarTip()
  }, [user?.id])

  async function cargar() {
    setCargando(true)
    const [ss, ah] = await Promise.all([
      getMisSuscripciones(user.id),
      getAhorroAcumulado(user.id),
    ])
    setSuscripciones(ss)
    setAhorro(ah)
    if (ss[0]) {
      const r = await getDashboardResumen(ss[0].id)
      setResumen(r)
    }
    setCargando(false)
  }

  async function cargarTip() {
    // 1) Intentamos leer el tip de hoy desde la tabla
    let { data, error } = await supabase.rpc('get_tip_hoy')
    if (error) { console.error('get_tip_hoy:', error); return }
    let tip = data?.[0]

    // 2) Si no hay tip de hoy, lo generamos llamando a la edge function
    if (!tip) {
      try {
        const { data: gen } = await supabase.functions.invoke('generar-tip-diario', { body: {} })
        if (gen?.success && gen?.tip) {
          // Re-leemos para obtener los datos del producto joineados
          const { data: data2 } = await supabase.rpc('get_tip_hoy')
          tip = data2?.[0]
        }
      } catch (e) {
        console.error('generar-tip-diario:', e)
      }
    }

    setTipDia(tip || null)
  }

  const tieneSuscripcion = suscripciones.length > 0
  const susc = suscripciones[0]
  const proximaFecha = resumen?.proximaEntregaReal || resumen?.proximaEntregaCalc
  const proximoPago = resumen?.proximoPago
  const ahorroAnio = ahorro?.ahorro_total_anio || 0
  const nombre = (profile?.nombre || user?.email || '').split(' ')[0].split('@')[0]
  const tipFallback = TIPS_FALLBACK[new Date().getDate() % TIPS_FALLBACK.length]
  const tip = tipDia || tipFallback
  const tipTieneProducto = tipDia?.producto_sku && tipDia?.producto_imagen_url

  // Bonificación 3 meses (devolución del mes 1 repartido en 2 y 3)
  const bonif = resumen?.bonif
  const bonifAplica = bonif?.aplica
  const m1 = Number(bonif?.montoFull || 0)
  const precioNormal = Number(bonif?.montoNormal || 0)
  const m23 = precioNormal - Number(bonif?.ahorroTotal || 0) / 2
  const total3meses = m1 + m23 * 2

  // Pago del mes siguiente (sobre la suscripción): si está en bonif mes 1 o 2 → m23 (mes 2/3 con descuento), si mes 3 → precio normal del mes 4, si no hay bonif → precio normal
  const pagoMesSig = bonifAplica
    ? (Number(bonif?.mesActual) >= 3 ? precioNormal : m23)
    : precioNormal

  // Ahorro de este mes: aproximación simple (anual / meses transcurridos del año)
  const _mesActual = new Date().getMonth() + 1
  const ahorroMes = ahorroAnio > 0 ? Math.round(Number(ahorroAnio) / _mesActual) : 0

  return (
    <ClienteLayout>
      <div className="i-wrap">
        {/* Hero saludo */}
        <div className="i-hero">
          <div className="i-greet">{getSaludo()},</div>
          <h1 className="i-hello">Hola <em>{nombre}</em> 👋</h1>
          {tieneSuscripcion ? (
            <p className="i-subhello">Tu próxima entrega ya está armada. Mirá qué viene o sumá productos.</p>
          ) : (
            <p className="i-subhello">Bienvenido a Limpack. Empezá a recibir tus productos de limpieza al mejor precio.</p>
          )}
        </div>

        {/* Strip próxima entrega o CTA suscribirse */}
        {tieneSuscripcion && proximaFecha ? (
          <Link to="/cliente/mi-plan" className="i-strip">
            <span className="i-strip-icon">📦</span>
            <div className="i-strip-info">
              <div className="i-strip-label">Próxima entrega</div>
              <div className="i-strip-title">{fmtFecha(proximaFecha)}</div>
              <div className="i-strip-sub">
                {susc.kit?.nombre || 'Tu kit'}
                {susc.kit?.tier && TIER_LINEA[susc.kit.tier] ? ` · ${TIER_LINEA[susc.kit.tier]}` : ''}
                {susc.barrio ? ` · ${susc.barrio}` : ''}
              </div>
            </div>
            <div className="i-strip-meta">
              <div className="i-strip-meta-cell">
                <div className="i-strip-meta-label">Ventana</div>
                <div className="i-strip-meta-val">10–14hs</div>
              </div>
              <div className="i-strip-meta-cell">
                <div className="i-strip-meta-label">Productos</div>
                <div className="i-strip-meta-val">13</div>
              </div>
            </div>
            <span className="i-strip-cta-btn">Ver detalle →</span>
          </Link>
        ) : !cargando && (
          <Link to="/cliente/nueva-suscripcion" className="i-strip i-strip-cta">
            <span className="i-strip-icon">✨</span>
            <div className="i-strip-info">
              <div className="i-strip-label">Empezá hoy</div>
              <div className="i-strip-title">Armá tu primera suscripción</div>
              <div className="i-strip-sub">10% abajo del súper · sin permanencia</div>
            </div>
            <span className="i-strip-cta-btn">Empezar →</span>
          </Link>
        )}

        {/* Bonificación 3 meses (solo si está activa) */}
        {bonifAplica && (
          <div className="i-bonif">
            <div className="i-bonif-head">
              <span className="i-bonif-tag">🎁 Bonificación primeros 3 meses</span>
              <span className="i-bonif-mes">Mes {bonif.mesActual} de 3</span>
            </div>
            <div className="i-bonif-explain">
              {bonif.mesActual === 1 && (
                <>El primer mes pagás <b>${m1.toLocaleString('es-UY')}</b>. Lo extra (<b>${(m1 - precioNormal).toLocaleString('es-UY')}</b>) te lo <b>devolvemos repartido en los meses 2 y 3</b> como descuento.</>
              )}
              {bonif.mesActual === 2 && (
                <>✅ Esta factura ya viene con <b>${(bonif.ahorroTotal/2).toLocaleString('es-UY')}</b> de descuento (devolución del mes 1). La del mes 3 también.</>
              )}
              {bonif.mesActual === 3 && (
                <>✅ Esta factura ya viene con <b>${(bonif.ahorroTotal/2).toLocaleString('es-UY')}</b> de descuento. Desde el mes 4 pagás el precio normal de suscriptor.</>
              )}
            </div>
            <div className="i-bonif-bar"><div className="i-bonif-bar-fill" style={{ width: `${(bonif.mesActual/3)*100}%` }}/></div>
            <div className="i-bonif-meses">
              <span className={bonif.mesActual >= 1 ? 'on' : ''}>Mes 1 · ${m1.toLocaleString('es-UY')}</span>
              <span className={bonif.mesActual >= 2 ? 'on' : ''}>Mes 2 · ${m23.toLocaleString('es-UY')} 💰</span>
              <span className={bonif.mesActual >= 3 ? 'on' : ''}>Mes 3 · ${m23.toLocaleString('es-UY')} 💰</span>
            </div>
            <div className="i-bonif-resumen">
              💡 <b>Total en 3 meses: ${total3meses.toLocaleString('es-UY')}</b> (promedio ${Math.round(total3meses/3).toLocaleString('es-UY')}/mes).
              Desde el <b>mes 4</b> pagás el precio normal de suscriptor: <b>${precioNormal.toLocaleString('es-UY')}/mes</b>.
            </div>
          </div>
        )}

        {/* Grid principal: shortcuts + aside */}
        <div className="i-grid">
          <div className="i-col-main">
            <h2 className="i-section">¿Qué querés hacer?</h2>
            <div className="i-shortcuts">
              <Link to="/cliente/tienda" className="i-shortcut feat">
                <div className="i-sc-emoji">🛒</div>
                <div className="i-sc-body">
                  <div className="i-sc-title">La Tienda Limpack</div>
                  <div className="i-sc-sub">Comprá productos sueltos. Los recibís el día de la semana que vos quieras.</div>
                </div>
              </Link>
              {tieneSuscripcion && (
                <Link to="/cliente/mi-plan" className="i-shortcut">
                  <div className="i-sc-emoji">✏️</div>
                  <div className="i-sc-body">
                    <div className="i-sc-title">Cambiar mi kit</div>
                    <div className="i-sc-sub">Pasate a otro tamaño o línea</div>
                  </div>
                </Link>
              )}
              <Link to="/cliente/vecinos" className="i-shortcut">
                <div className="i-sc-emoji">👥</div>
                <div className="i-sc-body">
                  <div className="i-sc-title">Invitar vecinos</div>
                  <div className="i-sc-sub">5% off al llegar a 10</div>
                </div>
              </Link>
            </div>
          </div>

          <aside className="i-col-aside">
            {tieneSuscripcion && (
              <>
                <h2 className="i-section">Tu cuenta</h2>
                <Link to="/cliente/pedidos" className="i-mini i-mini-split-card">
                  <div className="i-mini-icon">💳</div>
                  <div className="i-mini-body">
                    <div className="i-mini-split">
                      <div className="i-mini-half">
                        <div className="i-mini-label">Pago este mes</div>
                        <div className="i-mini-val">{proximoPago ? fmtMonto(proximoPago.monto) : '—'}</div>
                        <div className="i-mini-sub">{proximoPago?.vence ? `Vence ${fmtFecha(proximoPago.vence)}` : 'Sin pago pendiente'}</div>
                      </div>
                      <div className="i-mini-half">
                        <div className="i-mini-label">Mes siguiente</div>
                        <div className="i-mini-val">{fmtMonto(pagoMesSig)}</div>
                        <div className="i-mini-sub">Suscripción</div>
                      </div>
                    </div>
                    <div className="i-mini-iva">Precios c/IVA incluido</div>
                  </div>
                </Link>
                <Link to="/cliente/pedidos" className="i-mini i-mini-split-card">
                  <div className="i-mini-icon" style={{ background: '#dcfce7' }}>💰</div>
                  <div className="i-mini-body">
                    <div className="i-mini-split">
                      <div className="i-mini-half">
                        <div className="i-mini-label">Ahorrado este mes</div>
                        <div className="i-mini-val" style={{ color: 'var(--cl-green)' }}>{fmtMonto(ahorroMes)}</div>
                      </div>
                      <div className="i-mini-half">
                        <div className="i-mini-label">Este año</div>
                        <div className="i-mini-val" style={{ color: 'var(--cl-green)' }}>{fmtMonto(ahorroAnio)}</div>
                      </div>
                    </div>
                    <div className="i-mini-sub">vs Mercado · {new Date().getFullYear()}</div>
                  </div>
                </Link>
              </>
            )}

            <h2 className="i-section">Tip Limpack 💡</h2>
            {tipTieneProducto ? (
              <Link to={`/cliente/tienda?sku=${tipDia.producto_sku}`} className="i-tip i-tip-prod">
                <div className="i-tip-icon">{tip.icono || '💡'}</div>
                <div className="i-tip-text">
                  <b>{tip.titulo}.</b> {tip.texto}
                </div>
                <div className="i-tip-prod-img">
                  <img src={tipDia.producto_imagen_url} alt={tipDia.producto_nombre} />
                </div>
              </Link>
            ) : (
              <div className="i-tip">
                <div className="i-tip-icon">{tip.icono || tip.icon || '💡'}</div>
                <div className="i-tip-text">
                  <b>{tip.titulo}.</b> {tip.texto}
                </div>
              </div>
            )}
          </aside>
        </div>

        {/* Trust badge al final como footer de confianza */}
        <div className="i-trust">
          <TrustBadge variant="default" />
        </div>
      </div>

      <style>{`
        .i-wrap { max-width: 1320px; margin: 0 auto; padding: 16px 16px 32px }

        .i-hero { padding: 8px 4px 16px }
        .i-greet { font-size: 13px; color: var(--cl-muted); font-weight: 600 }
        .i-hello { font-size: 28px; font-weight: 900; color: var(--cl-navy); letter-spacing: -.02em; margin-top: 4px; line-height: 1.1 }
        .i-hello em { color: var(--cl-teal); font-style: normal }
        .i-subhello { font-size: 14px; color: var(--cl-muted); margin-top: 6px; line-height: 1.5 }

        .i-strip {
          display: flex; align-items: center; gap: 16px;
          background: linear-gradient(135deg, var(--cl-navy), var(--cl-navy-2));
          color: #fff; padding: 18px; border-radius: 18px;
          text-decoration: none;
          box-shadow: 0 10px 30px rgba(15,31,61,.18);
          transition: transform .15s, box-shadow .15s;
          margin-bottom: 8px;
        }
        .i-strip:hover { transform: translateY(-2px); box-shadow: 0 14px 36px rgba(15,31,61,.22) }
        .i-strip-cta { background: linear-gradient(135deg, var(--cl-blue), var(--cl-teal)) }
        .i-strip-icon { font-size: 32px }
        .i-strip-info { flex: 1 }
        .i-strip-label { font-size: 10px; font-weight: 800; opacity: .7; letter-spacing: .1em; text-transform: uppercase }
        .i-strip-title { font-size: 18px; font-weight: 900; margin-top: 4px; letter-spacing: -.01em }
        .i-strip-sub { font-size: 13px; opacity: .85; margin-top: 4px }
        .i-strip-meta { display: none }
        .i-strip-cta-btn {
          display: inline-flex; align-items: center;
          background: var(--cl-teal); color: var(--cl-navy);
          padding: 9px 16px; border-radius: 999px;
          font-size: 12px; font-weight: 800;
          flex-shrink: 0;
        }
        .i-strip-cta .i-strip-cta-btn { background: #fff; color: var(--cl-blue) }

        .i-grid { display: grid; grid-template-columns: 1fr; gap: 0 }
        .i-col-aside { display: flex; flex-direction: column }

        .i-shortcuts { display: grid; grid-template-columns: 1fr 1fr; gap: 10px }
        .i-shortcut {
          background: var(--cl-card); border: 1px solid var(--cl-line);
          padding: 16px; border-radius: 14px;
          display: flex; align-items: center; gap: 14px;
          text-decoration: none; color: var(--cl-navy);
          transition: transform .15s, border-color .15s, box-shadow .15s;
        }
        .i-shortcut:hover { transform: translateY(-2px); border-color: var(--cl-teal); box-shadow: 0 8px 20px rgba(15,31,61,.06) }
        .i-shortcut.feat {
          grid-column: 1 / -1;
          background: linear-gradient(135deg, var(--cl-blue), var(--cl-teal));
          color: #fff; border: 0;
          padding: 22px 24px;
          box-shadow: 0 12px 28px rgba(0,196,212,.25);
        }
        .i-shortcut.feat:hover { box-shadow: 0 16px 36px rgba(0,196,212,.32) }
        .i-sc-emoji { font-size: 30px; flex-shrink: 0 }
        .i-shortcut.feat .i-sc-emoji { font-size: 38px }
        .i-sc-body { flex: 1 }
        .i-sc-title { font-size: 15px; font-weight: 800; color: var(--cl-navy) }
        .i-shortcut.feat .i-sc-title { color: #fff; font-size: 20px; letter-spacing: -.01em }
        .i-sc-sub { font-size: 12.5px; color: var(--cl-muted); line-height: 1.45; margin-top: 3px }
        .i-shortcut.feat .i-sc-sub { color: rgba(255,255,255,.92); font-size: 14px; margin-top: 5px }

        .i-section { font-size: 16px; font-weight: 900; color: var(--cl-navy); letter-spacing: -.01em; margin: 22px 0 12px }
        .i-trust { margin-top: 32px; padding-top: 24px; border-top: 1px solid var(--cl-line) }

        /* Bonificación 3 meses */
        .i-bonif {
          margin-top: 12px;
          background: linear-gradient(135deg, #ecfeff, #f0fdfa);
          border: 1.5px solid #67e8f9; border-radius: 16px;
          padding: 18px 20px;
        }
        .i-bonif-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin-bottom: 8px }
        .i-bonif-tag { font-size: 13px; font-weight: 800; color: #0e7490; letter-spacing: -.01em }
        .i-bonif-mes { font-size: 11px; font-weight: 800; color: #fff; background: #0e7490; padding: 4px 10px; border-radius: 999px; letter-spacing: .04em; text-transform: uppercase }
        .i-bonif-explain { font-size: 13px; color: #155e75; line-height: 1.55; margin-bottom: 14px }
        .i-bonif-explain b { color: #0c4a6e; font-weight: 800 }
        .i-bonif-bar { background: rgba(14,116,144,.12); height: 8px; border-radius: 999px; overflow: hidden }
        .i-bonif-bar-fill { background: linear-gradient(90deg, #06b6d4, #14b8a6); height: 100%; transition: width .3s }
        .i-bonif-meses { display: flex; justify-content: space-between; gap: 8px; margin-top: 8px; font-size: 11px; font-weight: 700; color: #94a3b8; flex-wrap: wrap }
        .i-bonif-meses .on { color: #0e7490 }
        .i-bonif-resumen {
          margin-top: 12px; padding: 10px 12px;
          background: #f0f9ff; border: 1px solid #bae6fd;
          border-radius: 10px; font-size: 12px; color: #075985; line-height: 1.5;
        }
        .i-bonif-resumen b { color: #0c4a6e; font-weight: 800 }

        .i-mini {
          background: var(--cl-card); border: 1px solid var(--cl-line);
          padding: 16px; border-radius: 14px; text-decoration: none;
          color: var(--cl-navy); transition: border-color .15s, transform .15s;
          display: flex; gap: 12px; align-items: center;
          margin-bottom: 12px;
        }
        .i-mini:hover { border-color: var(--cl-teal); transform: translateY(-1px) }
        .i-mini-icon {
          width: 44px; height: 44px; border-radius: 12px;
          background: #e6fafc;
          display: flex; align-items: center; justify-content: center;
          font-size: 22px; flex-shrink: 0;
        }
        .i-mini-body { flex: 1 }
        .i-mini-label { font-size: 10px; color: var(--cl-muted2); font-weight: 800; letter-spacing: .08em; text-transform: uppercase }
        .i-mini-val { font-size: 22px; font-weight: 900; color: var(--cl-navy); margin-top: 2px; letter-spacing: -.02em }
        .i-mini-sub { font-size: 11px; color: var(--cl-muted); margin-top: 2px }

        /* Burbujas con 2 datos divididos */
        .i-mini-split-card { align-items: stretch }
        .i-mini-split-card .i-mini-icon { align-self: flex-start; margin-top: 4px }
        .i-mini-split { display: grid; grid-template-columns: 1fr 1fr; gap: 12px }
        .i-mini-half { padding-right: 12px; border-right: 1px solid var(--cl-line) }
        .i-mini-half:last-child { border-right: 0; padding-right: 0; padding-left: 4px }
        .i-mini-half .i-mini-val { font-size: 18px }
        .i-mini-iva { font-size: 10px; color: var(--cl-muted2); margin-top: 8px; padding-top: 8px; border-top: 1px dashed var(--cl-line); font-weight: 700; letter-spacing: .04em; text-transform: uppercase }

        .i-tip {
          background: linear-gradient(135deg, #fef3c7, #fde68a);
          border-radius: 14px; padding: 16px;
          display: flex; gap: 12px; align-items: flex-start;
        }
        .i-tip-icon { font-size: 28px; flex-shrink: 0 }
        .i-tip-text { font-size: 13.5px; color: #78350f; line-height: 1.55; flex: 1 }
        .i-tip-text b { color: #451a03; font-weight: 800 }
        .i-tip-prod {
          text-decoration: none; cursor: pointer;
          transition: transform .15s, box-shadow .15s;
          align-items: center; padding: 14px;
        }
        .i-tip-prod:hover { transform: translateY(-2px); box-shadow: 0 10px 24px rgba(252,211,77,.4) }
        .i-tip-prod-img {
          width: 56px; height: 70px; border-radius: 10px; flex-shrink: 0;
          background: rgba(255,255,255,.4); overflow: hidden;
          box-shadow: 0 4px 12px rgba(120,53,15,.12);
        }
        .i-tip-prod-img img { width: 100%; height: 100%; object-fit: cover; display: block }
        @media (min-width: 900px) {
          .i-tip-prod-img { width: 70px; height: 88px }
        }

        @media (min-width: 900px) {
          .i-wrap { padding: 36px 44px }
          .i-hero { padding: 0 0 24px }
          .i-greet { font-size: 14px }
          .i-hello { font-size: 44px }
          .i-subhello { font-size: 15px; margin-top: 8px }

          .i-strip { padding: 26px 28px; gap: 22px; margin-bottom: 4px }
          .i-strip-icon { font-size: 44px }
          .i-strip-label { font-size: 11px }
          .i-strip-title { font-size: 24px }
          .i-strip-sub { font-size: 14px }
          .i-strip-meta { display: flex; gap: 24px; padding: 0 8px; border-left: 1px solid rgba(255,255,255,.15); padding-left: 24px }
          .i-strip-meta-cell { display: flex; flex-direction: column; gap: 2px }
          .i-strip-meta-label { font-size: 10px; opacity: .65; font-weight: 700; letter-spacing: .08em; text-transform: uppercase }
          .i-strip-meta-val { font-size: 16px; font-weight: 800 }
          .i-strip-cta-btn { padding: 11px 22px; font-size: 13px }

          .i-grid { grid-template-columns: 1.6fr 1fr; gap: 36px; margin-top: 8px }
          .i-section { font-size: 18px; margin: 28px 0 14px }
          .i-shortcuts { gap: 14px }
          .i-shortcut { padding: 20px }
          .i-shortcut.feat { padding: 28px 30px }
          .i-sc-title { font-size: 16px }
          .i-shortcut.feat .i-sc-title { font-size: 24px }
          .i-sc-sub { font-size: 13px }
          .i-shortcut.feat .i-sc-sub { font-size: 15px }

          .i-mini { padding: 18px }
          .i-mini-icon { width: 48px; height: 48px; font-size: 24px }
          .i-mini-val { font-size: 26px }
          .i-mini-sub { font-size: 12px }
          .i-tip { padding: 20px 22px }
          .i-tip-text { font-size: 14px }
        }
      `}</style>
    </ClienteLayout>
  )
}
